declare global {
  interface Window {
    dataLayer?: unknown[]
    _mtm?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

// Matomo Tag Manager is loaded after the page becomes idle to avoid blocking
// the main thread during page load (improves TBT / Lighthouse mobile score).
export default defineNuxtPlugin(() => {
  const { MATOMO_CLOUD, MATOMO_CONTAINER } = useRuntimeConfig().public
  const isThankYouRoute = /^(?:\/tack|\/thank-you|\/gracias)(?:\/|$)/.test(window.location.pathname)
  const MATOMO_IDLE_TIMEOUT_MS = isThankYouRoute ? 3000 : 5000

  if (!MATOMO_CLOUD || !MATOMO_CONTAINER) return

  // Create global dataLayer and gtag immediately so inline conversion scripts
  // that fire on window load can safely call gtag before Matomo container loads.
  window.dataLayer = window.dataLayer || []
  const gtag =
    window.gtag ??
    (function () {
      // Keep the original snippet behavior: push the arguments object.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments)
    } as (...args: unknown[]) => void)
  window.gtag = gtag

  // A staff test page stays out of the production Matomo site. Thank-you URLs carry preview=false.
  const query = new URLSearchParams(window.location.search)
  if (['debug', 'preview'].some(name => query.has(name) && query.get(name) !== 'false')) return

  let hasLoaded = false

  const load = () => {
    if (hasLoaded) return
    hasLoaded = true
    if (document.getElementById('matomo-tagmanager')) return

    // Start the Matomo Tag Manager container
    const _mtm: unknown[] = (window._mtm = window._mtm || [])
    _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' })

    const script = document.createElement('script')
    script.id = 'matomo-tagmanager'
    script.setAttribute('data-cookieconsent', 'ignore')
    script.type = 'text/javascript'
    script.async = true
    script.src = `https://cdn.matomo.cloud/${MATOMO_CLOUD}/${MATOMO_CONTAINER}.js`
    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    } else {
      document.head.appendChild(script)
    }
  }

  const removeInteractionListeners = () => {
    window.removeEventListener('pointerdown', onFirstInteraction)
    window.removeEventListener('keydown', onFirstInteraction)
  }

  const onFirstInteraction = () => {
    load()
  }

  window.addEventListener('pointerdown', onFirstInteraction, { once: true, passive: true })
  window.addEventListener('keydown', onFirstInteraction, { once: true, passive: true })

  // Load on whichever happens first: user interaction or idle timeout. We
  // remove listeners in the idle path to avoid duplicate calls and leaks.
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      () => {
        removeInteractionListeners()
        load()
      },
      { timeout: MATOMO_IDLE_TIMEOUT_MS },
    )
  } else {
    setTimeout(() => {
      removeInteractionListeners()
      load()
    }, MATOMO_IDLE_TIMEOUT_MS)
  }
})
